import React from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  X, 
  Shield, 
  Settings, 
  Calendar, 
  TrendingUp, 
  Zap,
  Phone,
  FileText,
  MessageSquare,
  Award,
  Users
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card } from "../ui/card";

const AshaVsDentinaSlide = () => {
  const features = [
    { name: "AI Receptionist", asha: true, dentina: true, icon: Phone },
    { name: "AI Hygiene Recall Specialist", asha: true, dentina: false, icon: Calendar },
    { name: "AI Schedule Optimizer", asha: true, dentina: false, icon: Zap },
    { name: "AI Insurance Verification", asha: true, dentina: false, icon: Shield },
    { name: "AI Insurance Caller", asha: true, dentina: false, icon: Phone },
    { name: "AI Fax Manager", asha: true, dentina: false, icon: FileText },
    { name: "AI Patient Experience Agent", asha: true, dentina: false, icon: TrendingUp },
    { name: "AI Case Acceptance Agent", asha: true, dentina: false, icon: Award },
    { name: "VoIP & Patient Messaging", asha: true, dentina: false, icon: MessageSquare },
    { name: "Next-Gen Forms & eFax", asha: true, dentina: false, icon: FileText },
  ];

  const companyComparison = [
    { 
      metric: "HIPAA Compliance", 
      asha: "Fully Compliant", 
      dentina: "Not Compliant", 
      icon: Shield
    },
    { 
      metric: "Venture Backing", 
      asha: "$4.4M Capital Raised", 
      dentina: "Zero Venture Backing", 
      icon: TrendingUp
    },
    { 
      metric: "AI Technology", 
      asha: "Built In-House", 
      dentina: "Outsourced / Wrapper", 
      icon: Zap
    },
    { 
      metric: "Team Expertise", 
      asha: "AI Experts from Top Institutions", 
      dentina: "Limited / Unknown", 
      icon: Users
    }
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-8 lg:p-12 font-sans overflow-hidden">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center flex-shrink-0 mb-6"
      >
        <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
          Asha Health <span className="text-slate-400 font-light mx-4">vs</span> Dentina
        </h2>
        
        {/* TLDR Callout - Moved right below title */}
        <div className="bg-blue-600 rounded-2xl p-4 text-white shadow-md border border-blue-500 max-w-5xl mx-auto">
            <div className="flex items-center gap-4 justify-center">
                <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                    <Zap className="w-5 h-5 text-yellow-300" />
                </div>
                <p className="text-lg md:text-xl font-medium leading-tight text-center">
                    <span className="font-bold underline decoration-yellow-400 underline-offset-4">TL;DR:</span> Asha Health is a <span className="text-yellow-200 font-bold">one-stop shop</span> for all the AI agents you need, whereas Dentina <span className="opacity-80">only sells you an AI receptionist.</span>
                </p>
            </div>
        </div>
      </motion.div>

      <div className="flex-1 flex gap-8 min-h-0">
        
        {/* LEFT: Platform Capabilities */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-[50%] flex flex-col"
        >
           <Card className="h-full border-slate-200 shadow-sm overflow-hidden flex flex-col bg-white">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-700">Platform Capabilities</h3>
                  <div className="flex gap-12 pr-4">
                      <div className="w-16 text-center font-bold text-blue-600 text-lg">Asha</div>
                      <div className="w-16 text-center font-bold text-slate-400 text-lg">Dentina</div>
                  </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                  <Table>
                      <TableBody>
                          {features.map((feature, idx) => (
                              <TableRow key={idx} className="hover:bg-slate-50 border-b-slate-50 last:border-0">
                                  <TableCell className="font-medium text-slate-700 text-base py-3 pl-4 flex items-center gap-3">
                                      <div className={`p-1 rounded-md ${feature.asha && !feature.dentina ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                          <feature.icon className="w-4 h-4" />
                                      </div>
                                      {feature.name}
                                  </TableCell>
                                  <TableCell className="w-16 text-center">
                                      <div className="flex justify-center">
                                          {feature.asha ? (
                                              <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center">
                                                  <Check className="w-4 h-4 text-green-600" />
                                              </div>
                                          ) : (
                                              <X className="w-4 h-4 text-slate-300" />
                                          )}
                                      </div>
                                  </TableCell>
                                  <TableCell className="w-16 text-center pr-6">
                                      <div className="flex justify-center">
                                          {feature.dentina ? (
                                              <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center">
                                                  <Check className="w-4 h-4 text-slate-500" />
                                              </div>
                                          ) : (
                                              <X className="w-4 h-4 text-red-200" />
                                          )}
                                      </div>
                                  </TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
              </div>
           </Card>
        </motion.div>

        {/* RIGHT: Company Comparison */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-[50%] flex flex-col"
        >
           <Card className="h-full border-slate-200 shadow-sm overflow-hidden flex flex-col bg-white">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="font-bold text-lg text-slate-700">Company & Trust Comparison</h3>
              </div>
              <div className="flex-1 overflow-auto p-0">
                  <Table>
                      <TableHeader>
                          <TableRow className="hover:bg-transparent border-b-slate-100">
                              <TableHead className="w-[30%] pl-6">Metric</TableHead>
                              <TableHead className="w-[35%] text-blue-600 font-bold">Asha Health</TableHead>
                              <TableHead className="w-[35%] text-slate-500 font-bold">Dentina</TableHead>
                          </TableRow>
                      </TableHeader>
                      <TableBody>
                          {companyComparison.map((item, idx) => (
                              <TableRow key={idx} className="hover:bg-slate-50 border-b-slate-50 last:border-0">
                                  <TableCell className="font-medium text-slate-700 py-5 pl-6 flex items-center gap-2">
                                      <item.icon className="w-4 h-4 text-slate-400" />
                                      {item.metric}
                                  </TableCell>
                                  <TableCell className="font-bold text-slate-900 py-5 text-base">
                                      {item.asha}
                                  </TableCell>
                                  <TableCell className="font-medium text-slate-500 py-5 text-base">
                                      {item.dentina}
                                  </TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
              </div>
              <div className="p-6 bg-slate-50 mt-auto border-t border-slate-100">
                  <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                      <p className="text-sm text-slate-600 leading-relaxed">
                          Asha Health provides an <span className="font-bold text-slate-900">enterprise-grade infrastructure</span> built by AI experts from top institutions and chief medical officers, ensuring long-term stability and security for your practice.
                      </p>
                  </div>
              </div>
           </Card>
        </motion.div>

      </div>
    </div>
  );
};

export default AshaVsDentinaSlide;
